import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * VideoControls - Playback controls for video mockups
 *
 * Displays play/pause, replay, and progress bar controls.
 * Designed to sit below the mockup preview to keep the mockup pixel-perfect.
 *
 * Usage:
 *   <VideoControls videoRef={videoRef} />
 *
 * Or with a container ref (will find the video element inside):
 *   <VideoControls containerRef={containerRef} />
 */

interface VideoControlsProps {
  /** Direct ref to a video element */
  videoRef?: React.RefObject<HTMLVideoElement>;
  /** Ref to a container that contains a video element */
  containerRef?: React.RefObject<HTMLElement>;
  /** Whether to show the controls (hide when no video) */
  show?: boolean;
  /** Current video source URL - triggers effect re-run when source changes */
  videoSrc?: string | null;
  /** Target a specific carousel card's video by index (uses data-card-index attribute) */
  cardIndex?: number | null;
}

export function VideoControls({ videoRef, containerRef, show = true, videoSrc, cardIndex }: VideoControlsProps) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const progressRef = useRef<HTMLDivElement>(null);

  // Find the video element
  const getVideoElement = useCallback((): HTMLVideoElement | null => {
    if (videoRef?.current) {
      return videoRef.current;
    }
    if (containerRef?.current) {
      // If a specific card index is given, find the video within that card
      if (cardIndex != null) {
        const card = containerRef.current.querySelector(`[data-card-index="${cardIndex}"]`);
        return card?.querySelector('video') ?? null;
      }
      return containerRef.current.querySelector('video');
    }
    return null;
  }, [videoRef, containerRef, cardIndex]);

  useEffect(() => {
    const video = getVideoElement();
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsPlaying(!video.paused);
      setIsMuted(video.muted);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // Initial state
    if (video.readyState >= 1) {
      setDuration(video.duration);
      setIsPlaying(!video.paused);
      setIsMuted(video.muted);
    }

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [getVideoElement, videoSrc]);

  const togglePlay = () => {
    const video = getVideoElement();
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const replay = () => {
    const video = getVideoElement();
    if (!video) return;

    video.currentTime = 0;
    video.play();
  };

  const toggleMute = () => {
    const video = getVideoElement();
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = getVideoElement();
    if (!video || !progressRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    video.currentTime = percentage * duration;
  };

  const formatTime = (time: number): string => {
    if (!isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!show) return null;

  return (
    <div className="w-full p-3 mt-4 bg-gray-100 border border-gray-200 rounded-lg">
      {/* Progress bar */}
      <div
        ref={progressRef}
        className="w-full h-1.5 bg-gray-300 rounded cursor-pointer mb-3"
        onClick={handleProgressClick}
      >
        <div
          className="h-full bg-blue-500 rounded transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between">
        {/* Left: Play/Pause and Replay */}
        <div className="flex items-center gap-2">
          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-700 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Replay */}
          <button
            onClick={replay}
            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
            title="Replay"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Mute/Unmute */}
          <button
            onClick={toggleMute}
            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
          </button>
        </div>

        {/* Right: Time display */}
        <div className="text-gray-600 text-sm font-mono">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
    </div>
  );
}
